import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { Divider, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSnackbar } from 'notistack';

import KenSectionContent from './Components/KenSectionContent';
import KenStepperFooter from '../KenStepperFooter';
import KenLoader from '../../../KenLoader';
import { createComposite } from '../../../../Utils/apiServices';
import { FormContext } from '../../../../Context/formContext';
import { TABLET_BREAKPOINT } from '../../../../Constants/constant';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    height: 'calc(100% - 16px)',
    paddingTop: theme.spacing(7.125),
    overflow: 'auto',
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      paddingTop: theme.spacing(3),
    },
  },
  sectionLabel: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  footerContainer: {
    position: 'absolute',
    bottom: '0px',
    padding: '16px',
    left: '0px',
    right: '0px',
    overflow: 'hidden',
    background: '#FFFFFF',
    minHeight: 76,
  },
  required: {
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '100%',
    color: '#393939',
    '& > .required': {
      color: '#FC2C32',
    },
  },
}));

function KenStepperContent(props) {
  const {
    currentStepDetails,
    completedSteps,
    handleComplete,
    handleBack,
    handleSave,
    handleNext,
    activeStep,
    formJSON,
    completed,
    allStepsCompleted,
    handleStepsSubmit,
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down(TABLET_BREAKPOINT));
  const { formState, setFormState } = useContext(FormContext);
  const [sections, setSections] = useState(currentStepDetails?.sections);
  const [formValid, setFormValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const contactIdRef = useRef(null);

  const methods = useForm({
    defaultValues: { ...formState.formData },
    mode: 'onTouched',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldFocusError: true,
  });

  const control = methods.control;

  const watchFields = useWatch({
    control,
    name: [
      'addresses[0][mailingCountry]',
      'addresses[0][mailingState]',
      'addresses[0][mailingDistrict]',
    ],
  });

  useEffect(() => {
    setSections(currentStepDetails?.sections);
  }, [currentStepDetails]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  const onError = () => {
    enqueueSnackbar('Please fill in the mandatory fields', {
      variant: 'warning',
    });
  };

  const isFormValid = (isValid) => {
    setFormValid(isValid);
  };

const saveApplicationData = async (formValues) => {
    if (!formValues) return null;

    const {
      applicant,
      programDetails,
      application,
      hed__Address__c,
      documents,          
      Resume,            
      adharCardUpload,
      panCardUpload,
      qualificationUpload,
      passportSizePhotograph,
      passbookCopy,
      ...payload         
    } = formValues;

    if (Array.isArray(hed__Address__c) && hed__Address__c.length > 0) {
      Object.assign(payload, hed__Address__c[0]);
    }

    payload.AccountId = "001Ii00000At6cjIAB";
    payload.RecordTypeId = "012Ii000000L2rfIAC";

    // 🔥 Collect uploaded files
    const uploads =
      documents ||
      Object.values(formValues)
        .filter((v) => Array.isArray(v))
        .flat()
        .filter((f) => f?.base64);

    const compositePayload = {
      allOrNone: true,
      compositeRequest: [
        {
          method: "POST",
          url: "/services/data/v62.0/sobjects/Contact",
          referenceId: "newContact",
          body: payload, // ✅ now clean, no Resume field
        },
        ...uploads.map((file, idx) => ({
          method: "POST",
          url: "/services/data/v62.0/sobjects/ContentVersion",
          referenceId: `file${idx + 1}`,
          body: {
            Title: file.fileName.replace(/\.[^/.]+$/, ""),
            PathOnClient: file.fileName,
            VersionData: file.base64.replace(/^data:.*;base64,/, ''),
            FirstPublishLocationId: "@{newContact.id}",
          },
        })),
      ],
    };

    try {
      setLoading(true); // 🚀 show loader
      const res = await createComposite(compositePayload);
      return res;
    } catch (err) {
      console.error("Save application failed", err);
      throw err;
    } finally {
      setLoading(false); // ✅ hide loader
    }
  };

const handleSectionSubmit = (goNext) => {
  methods.handleSubmit(
    async (formValues) => {
      // ✅ Runs only if form is valid
      const id = await saveApplicationData(formValues);

      if (id) {
        // 🔥 Navigate immediately after saving
        navigate('/thankYou');
      }
    },
    (errors) => {
      // ❌ Runs on validation error
      onError();
      console.warn('Validation errors:', errors);
    }
  )();
};


  const getStepContentByType = () => {
    return (
      <Box className={classes.formContainer}>
        {currentStepDetails?.showStepName && (
          <p style={{ textAlign: 'left' }}>{currentStepDetails?.stepName}</p>
        )}

        <FormProvider {...methods}>
          <form
            id={`form-step${activeStep}`}
            onSubmit={(e) => {
              e.preventDefault();
              handleSectionSubmit(activeStep !== formJSON.length - 1);
            }}
            style={{ paddingBottom: '140px' }}
          >
            {sections?.map((section, index) => (
              <React.Fragment key={section.sectionId}>
                {section.sectionVisible && (
                  <Box margin={isMobileScreen ? '0px 16px' : '0px 318px'}>
                    {section?.sectionName && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom={theme.spacing(4)}
                      >
                        <Typography
                          align="left"
                          className={classes.sectionLabel}
                          fontSize="20px"
                        >
                          {section?.sectionName}
                        </Typography>
                        {section?.requiredHint && !isMobileScreen && (
                          <Typography className={classes.required}>
                            Fields marked with <span className="required">*</span> are required
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Box>
                      <KenSectionContent
                        {...props}
                        fields={section?.fields}
                        onFormSubmit={() => {}}
                        isFormValid={isFormValid}
                      />
                    </Box>
                    {sections?.length - 1 !== index && (
                      <Box mt={3} mb={6}>
                        <Divider />
                      </Box>
                    )}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </form>

          <KenStepperFooter
            completedSteps={completedSteps}
            handleComplete={handleComplete}
            handleBack={handleBack}
            handleSave={handleSave}
            handleNext={() => handleSectionSubmit(true)}
            activeStep={activeStep}
            steps={formJSON}
            stepsCount={formJSON.length}
            completed={completed}
            allStepsCompleted={allStepsCompleted}
            handleStepsSubmit={() => handleSectionSubmit(false)}
            formValid={formValid}
          />
        </FormProvider>
      </Box>
    );
  };

  return (
    <>
      <div id="content">
        {loading && <KenLoader />}
        {getStepContentByType(currentStepDetails?.stepType)}
      </div>
    </>
  );
}

export default KenStepperContent;
